/**
 * Gamification Service
 * Tracks badges earned through user actions
 *
 * Storage: server-backed via the badges API when VITE_API_BASE_URL is set —
 * earned badges roam across devices and are visible to colleagues. Action
 * counters stay per-device. Pure localStorage in dev/mock mode.
 */
import { getProfile } from './profileService';

const STORAGE_KEY = 'peoplevue_badges';
const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Stashed by syncBadges; lets awardBadge push the signed-in user's badges to
// the server without threading a token through every call site.
let apiToken = null;
let currentUserId = null;

/**
 * Pull everyone's earned badges from the server into the local cache so
 * badges show on colleagues' profiles, then remember the token for pushes.
 * Call once after sign-in. Non-fatal on failure.
 */
export async function syncBadges(token, userId = null) {
  if (!API_BASE || !token) return false;
  apiToken = token;
  if (userId) currentUserId = userId;
  try {
    const res = await fetch(`${API_BASE}/badges`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`Badges API ${res.status}`);
    const serverMap = await res.json();
    Object.entries(serverMap || {}).forEach(([uid, entry]) => {
      const local = getUserBadges(uid);
      const merged = [...new Set([...(entry.badges || []), ...local.earned])];
      saveUserBadges(uid, { ...local, earned: merged }, { skipPush: true });
    });
    return true;
  } catch (err) {
    console.warn('Failed to sync badges (non-fatal):', err.message);
    return false;
  }
}

function pushBadges(userId, badges) {
  // Server policy: users may only write their own badge record.
  if (!API_BASE || !apiToken || (currentUserId && userId !== currentUserId)) return;
  fetch(`${API_BASE}/badges/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiToken}` },
    body: JSON.stringify({ badges: badges.earned, points: badges.earned.length * 10 }),
  })
    .then((res) => { if (!res.ok) console.warn(`Badges push failed: ${res.status}`); })
    .catch((err) => console.warn('Badges push failed:', err.message));
}

/**
 * Badge IDs that used to exist and were deliberately removed (2026-07-24).
 * Kept as a list, not deleted silently, because earned badges are persisted
 * server-side and cached in localStorage — live users still hold these IDs, so
 * every read has to filter them out rather than render an undefined badge.
 *
 * Why each one went:
 * - ghosted: "returned after 30+ days of absence" describes medical, parental,
 *   FMLA, bereavement and military leave far more often than a vacation. It
 *   publicly badged a protected absence on a colleague-visible profile. Removed
 *   outright, with no visual replacement.
 * - first_meeting, group_organizer: unobtainable — the 'schedule_meeting'
 *   action was never dispatched anywhere in src/.
 * - the rest: participation, not contribution. Each fired on a single
 *   unavoidable interaction, so within a week every employee held the same set
 *   and the badge carried no information.
 */
export const REMOVED_BADGE_IDS = new Set([
  'ghosted',
  'first_meeting',
  'group_organizer',
  'first_message',
  'first_call',
  'first_email',
  'first_search',
  'profile_viewer',
  'org_chart_explorer',
]);

// All available achievement badges
export const ACHIEVEMENT_BADGES = {
  // Profile completion badges
  FIRST_BIO: {
    id: 'first_bio',
    name: 'Storyteller',
    icon: '📝',
    description: 'Added a bio to your profile',
    category: 'profile',
  },
  FIRST_SKILLS: {
    id: 'first_skills',
    name: 'Skill Sharer',
    icon: '🎯',
    description: 'Added at least five expertise items to your profile',
    category: 'profile',
  },
  PROFILE_COMPLETE: {
    id: 'profile_complete',
    name: 'Profile Pro',
    icon: '🌟',
    description: 'Completed a strong, profile-ready PeopleVUE profile',
    category: 'profile',
  },
  CUSTOM_BANNER: {
    id: 'custom_banner',
    name: 'Banner Boss',
    icon: '🎨',
    description: 'Customized your profile banner',
    category: 'profile',
  },
  CONNECTOR: {
    id: 'connector',
    name: 'Connector',
    icon: '🤝',
    description: 'Opened at least two collaboration preferences on your profile',
    category: 'profile',
  },
  CROSS_POLLINATOR: {
    id: 'cross_pollinator',
    name: 'Cross-Pollinator',
    icon: '🌐',
    description: 'Added expertise, skills, and software so your profile spans multiple lenses',
    category: 'profile',
  },
  MENTOR: {
    id: 'mentor',
    name: 'Mentor',
    icon: '🧭',
    description: 'Turned on mentoring and listed topics you can help with',
    category: 'profile',
  },
  SPEAKER_CIRCUIT: {
    id: 'speaker_circuit',
    name: 'Speaker Circuit',
    icon: '🎤',
    description: 'Opened yourself to speaking and backed it up with real expertise',
    category: 'profile',
  },
  PROJECT_SHOWCASE: {
    id: 'project_showcase',
    name: 'Project Showcase',
    icon: '🏗️',
    description: 'Added five MOREgroup projects to your profile',
    category: 'profile',
  },
  KNOWLEDGE_BASE: {
    id: 'knowledge_base',
    name: 'Knowledge Base',
    icon: '🧠',
    description: 'Built a deep, searchable profile colleagues can actually use',
    category: 'profile',
  },
};

// Every badge ID that still has a definition
function isKnownBadgeId(badgeId) {
  return Object.values(ACHIEVEMENT_BADGES).some((b) => b.id === badgeId);
}

// Get all badges for a user.
// Removed badge IDs are filtered at the read boundary rather than migrated out
// of storage — nothing destructive runs against a user's record, and a badge
// that no longer has a definition can never reach the UI as `undefined`.
export function getUserBadges(userId) {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_${userId}`);
    const badges = stored ? JSON.parse(stored) : { earned: [], stats: {} };
    return {
      ...badges,
      earned: (badges.earned || []).filter(isKnownBadgeId),
    };
  } catch (e) {
    console.warn('Failed to load badges:', e);
    return { earned: [], stats: {} };
  }
}

// Save badges for a user
function saveUserBadges(userId, badges, { skipPush = false } = {}) {
  try {
    localStorage.setItem(`${STORAGE_KEY}_${userId}`, JSON.stringify(badges));
    if (!skipPush) pushBadges(userId, badges);
  } catch (e) {
    console.warn('Failed to save badges:', e);
  }
}

// Award a badge (returns the badge if newly earned, null if already had)
export function awardBadge(userId, badgeId) {
  const badges = getUserBadges(userId);
  const badge = Object.values(ACHIEVEMENT_BADGES).find(b => b.id === badgeId);

  if (!badge) {
    console.warn(`Unknown badge: ${badgeId}`);
    return null;
  }

  // Check if already earned
  if (badges.earned.includes(badgeId)) {
    return null; // Already have it
  }

  // Award the badge
  badges.earned.push(badgeId);
  badges.stats[badgeId] = {
    earnedAt: new Date().toISOString(),
  };

  saveUserBadges(userId, badges);

  return badge; // Return the badge for celebration
}

// Check if user has a badge
export function hasBadge(userId, badgeId) {
  const badges = getUserBadges(userId);
  return badges.earned.includes(badgeId);
}

// Track an action and potentially award a badge
// `_data` is unused since the group_organizer attendeeCount check was removed,
// but the third parameter stays so existing call sites are untouched.
function countUnique(values = []) {
  return new Set(
    values.map((value) => String(value || '').trim()).filter(Boolean),
  ).size;
}

function evaluateProfileBadges(userId, profile = getProfile(userId)) {
  const earned = [];
  const expertiseCount = countUnique([
    ...(profile.skills || []),
    ...(profile.areasOfExpertise || []),
    ...(profile.programsExpertise || []),
  ]);
  const collaborationOpenCount = [
    Boolean(profile.openToSpeaking),
    Boolean(profile.openToWhitePaper),
    Boolean(profile.openToMentoring),
  ].filter(Boolean).length;
  const hasProfileDepth = Boolean(
    profile.practiceArea
      || profile.statesLicensed?.length
      || profile.certifications?.length
      || profile.collegeAlmaMater
      || profile.highSchoolAlmaMater
      || profile.volunteerAt
      || profile.interests?.length
      || profile.moregroupProjects?.length
      || profile.priorProjects?.trim()
      || profile.mentorTopics?.trim(),
  );

  if (profile.bio?.trim()) earned.push(awardBadge(userId, 'first_bio'));
  if (profile.banner) earned.push(awardBadge(userId, 'custom_banner'));
  if (expertiseCount >= 5) earned.push(awardBadge(userId, 'first_skills'));
  if (collaborationOpenCount >= 2) earned.push(awardBadge(userId, 'connector'));
  if ((profile.areasOfExpertise?.length || 0) > 0 && (profile.skills?.length || 0) > 0 && (profile.programsExpertise?.length || 0) > 0) {
    earned.push(awardBadge(userId, 'cross_pollinator'));
  }
  if (profile.openToMentoring && profile.mentorTopics?.trim()) earned.push(awardBadge(userId, 'mentor'));
  if (profile.openToSpeaking && expertiseCount >= 5) earned.push(awardBadge(userId, 'speaker_circuit'));
  if ((profile.moregroupProjects?.length || 0) >= 5) earned.push(awardBadge(userId, 'project_showcase'));
  if (profile.bio?.trim() && expertiseCount >= 5 && (profile.programsExpertise?.length || 0) >= 3 && ((profile.moregroupProjects?.length || 0) >= 1 || profile.priorProjects?.trim())) {
    earned.push(awardBadge(userId, 'knowledge_base'));
  }
  if (profile.bio?.trim() && expertiseCount >= 5 && hasProfileDepth) {
    earned.push(awardBadge(userId, 'profile_complete'));
  }

  return earned.filter(Boolean);
}

export function trackAction(userId, action, _data = {}) {
  const data = _data || {};
  const badges = getUserBadges(userId);

  // Initialize counters if needed
  if (!badges.stats.counts) {
    badges.stats.counts = {};
  }

  // Increment action counter
  badges.stats.counts[action] = (badges.stats.counts[action] || 0) + 1;
  saveUserBadges(userId, badges);

  // Check for badge awards based on action
  let earnedBadge = null;

  switch (action) {
    case 'add_bio':
      break;
    case 'add_skills':
      break;
    case 'custom_banner':
      break;
    // No cases for send_message / make_call / send_email / search /
    // view_profile / view_org_chart / schedule_meeting. Those badges were
    // removed (see REMOVED_BADGE_IDS). The actions still increment their
    // counters above — call sites are unchanged and simply earn nothing.
  }

  // Profile badges are data-driven rather than click-driven: actions tell us
  // when to re-evaluate, but the unlocked state comes from the saved profile.
  const [firstEarned] = evaluateProfileBadges(userId, data.profile || getProfile(userId));
  earnedBadge = firstEarned || earnedBadge;

  return earnedBadge;
}

// Get all earned badges with their details
export function getEarnedBadgesDetails(userId) {
  const { earned, stats } = getUserBadges(userId);
  return earned
    .map(badgeId => {
      const badge = Object.values(ACHIEVEMENT_BADGES).find(b => b.id === badgeId);
      return badge ? { ...badge, earnedAt: stats[badgeId]?.earnedAt } : null;
    })
    .filter(Boolean);
}

// Reset badges for a user (for testing)
export function resetBadges(userId) {
  localStorage.removeItem(`${STORAGE_KEY}_${userId}`);
}

// Get badge categories
export function getBadgesByCategory() {
  const categories = {};
  Object.values(ACHIEVEMENT_BADGES).forEach(badge => {
    if (!categories[badge.category]) {
      categories[badge.category] = [];
    }
    categories[badge.category].push(badge);
  });
  return categories;
}
